import { userIdHeaderName } from "./authUtils"

const API_BASE_URL = `${window.__RUNTIME_CONFIG__.REACT_APP_API}`

// Request timeout. The handler does minimal work (Arango lookup + JWT sign)
// so anything longer than a few seconds indicates a real problem.
const REQUEST_TIMEOUT_MS = 15000

/**
 * Start a manager role-assumption session.
 *
 * Hits the Hasura REST endpoint `role-assumption/start`, which routes to
 * server-node's /startRoleAssumption handler. The handler validates the
 * caller is a manager and that the target organization exists, then mints
 * a short-lived (HS256, ~30 min) JWT carrying the manager's identity but
 * the chosen role + org. The client uses this token for all subsequent
 * API calls until expiry or explicit stop.
 *
 * Resolves with the parsed `result` envelope from Hasura. Throws an Error
 * with a user-readable `.message` on any failure (network, timeout,
 * non-200, malformed body).
 */
export const startRoleAssumption = async (accessToken, userId, { targetOrgId, targetRole }) => {
  if (!targetOrgId || !targetRole) {
    throw new Error("Both organization and role are required")
  }

  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)

  let res
  try {
    // Authenticate as the REAL manager — never via the role-assumption
    // header swap that authHeadersFor() applies. The /startRoleAssumption
    // Hasura action is `manager`-only; if an assumption is already active,
    // authHeadersFor would omit Authorization and send the HS256 assumed
    // token (default-role: sponsor), which Hasura would reject. Today the
    // UI gates "Assume role" on no-active-assumption so this scenario
    // doesn't surface, but the API call's contract is "always the real
    // manager" — encode that directly here.
    res = await fetch(`${API_BASE_URL}/role-assumption/start`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        [userIdHeaderName]: userId,
        "content-type": "application/json",
      },
      body: JSON.stringify({ targetOrgId, targetRole }),
      signal: controller.signal,
    })
  } catch (err) {
    if (err?.name === "AbortError") {
      throw new Error("The request timed out. Please try again.")
    }
    throw new Error(`Network error contacting the server: ${err?.message ?? "unknown"}`)
  } finally {
    clearTimeout(timeoutId)
  }

  if (res.status === 200) {
    let body
    try {
      body = await res.json()
    } catch {
      throw new Error("Server returned an invalid response body")
    }
    const result = body?.result ?? body
    if (!result?.token || !result?.expiresAt || !result?.assumed) {
      throw new Error("Server returned an invalid assumption token")
    }
    return result
  }

  // Non-200: try to surface the server's message, fall back to a generic one.
  let message = `Failed to start role assumption (HTTP ${res.status})`
  try {
    const errBody = await res.json()
    if (errBody?.message) message = errBody.message
    else if (errBody?.error) message = errBody.error
  } catch {
    // ignore parse failure
  }
  throw new Error(message)
}
